// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import { useCallback, useState } from 'react';
import { resetUserPassword } from '../services/userService';

export interface ResetPasswordView {
  /** Resolves to the temporary password. Show it once; never store it. */
  resetPassword: (userId: string) => Promise<string>;
  isResetting: boolean;
}

/**
 * Generates a temporary password for a local user (AB#35253). IAM replaces
 * their password and signs them out everywhere.
 *
 * Deliberately not a react-query mutation: a mutation keeps its result in the
 * QueryClient's mutation cache for as long as the component is mounted, where
 * devtools and any cache reader can see it. Calling the service directly
 * leaves the password only in the promise this returns.
 */
export function useResetPassword(): ResetPasswordView {
  const [isResetting, setIsResetting] = useState(false);

  const resetPassword = useCallback(async (userId: string) => {
    setIsResetting(true);
    try {
      return (await resetUserPassword(userId)).temporary_password;
    } finally {
      setIsResetting(false);
    }
  }, []);

  return { resetPassword, isResetting };
}
